import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { XMarkIcon, CheckIcon, PencilIcon } from '@heroicons/react/24/solid';
import { HiOutlineCheck } from 'react-icons/hi';
import { CATEGORY_COLORS, taskService, settingsService } from '../services/database';

interface CategoryFilterPopupProps {
  selectedCategory: string | undefined;
  onChange: (category: string | undefined) => void;
  trigger: React.ReactElement; // The button that opens the popover
  onCategoryRenamed?: (oldName: string, newName: string) => void;
  initiateCategoryEdit: (categoryName: string) => void;
  refreshKey: number; // Add the refresh key prop type
  allowedCategories?: string[]; // Add prop to limit displayed categories
  onOpenChange?: (isOpen: boolean) => void; // Callback for popup open/close state
}

const CategoryFilterPopup: React.FC<CategoryFilterPopupProps> = ({
  selectedCategory,
  onChange,
  trigger,
  onCategoryRenamed,
  initiateCategoryEdit,
  refreshKey, // Destructure the new prop
  allowedCategories, // Destructure allowed categories
  onOpenChange, // Destructure the new callback
}) => {
  const [categories, setCategories] = useState<string[]>([]);
  const [hoverCategory, setHoverCategory] = useState<string | null>(null);
  
  // State for editing categories
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // Function to load and filter categories
  const loadCategories = async () => {
    try {
      // If allowedCategories is provided, use it directly
      if (allowedCategories && allowedCategories.length > 0) {
        setCategories(allowedCategories);
        return;
      }
      
      // Otherwise, load from database (fallback for when no allowedCategories)
      const deletedCategories = await settingsService.getSetting<string[]>('deletedCategories', []);
      const filteredPredefined = Object.keys(CATEGORY_COLORS).filter(
        (cat) => !deletedCategories.includes(cat)
      );
      const existingCategories = await taskService.getAllCategories();
      const allCategories = Array.from(new Set([...filteredPredefined, ...existingCategories])).sort();
      setCategories(allCategories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  // Load categories when the popover might open OR when the refresh key or allowedCategories change
  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, allowedCategories]); // Reload when refreshKey or allowedCategories change

  // Focus input when editing category
  useEffect(() => {
    if (editingCategory && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCategory]);

  const handleCategoryClick = (category: string | undefined, close: () => void) => {
    // If the clicked category is already selected, clear the filter (pass undefined)
    // Otherwise, set the filter to the clicked category
    onChange(selectedCategory === category ? undefined : category);
    // Close the popup after selection/deselection
    close();
  };

  const handleEditCategory = (
    e: React.MouseEvent,
    categoryName: string,
    close: () => void
  ) => {
    e.stopPropagation(); // Prevent category selection
    setEditingCategory(categoryName);
    setEditingName(categoryName);
  };

  const handleRenameCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory || !editingName.trim()) return;

    const newName = editingName.trim();
    if (newName === editingCategory) {
      setEditingCategory(null);
      setEditingName('');
      return;
    }

    try {
      await taskService.renameCategory(editingCategory, newName);
      
      // If the currently selected category is being renamed, update the selection
      if (selectedCategory === editingCategory) {
        onChange(newName);
      }

      // Reload categories
      await loadCategories();
      
      setEditingCategory(null);
      setEditingName('');

      // Notify parent component that a category was renamed
      if (onCategoryRenamed) {
        onCategoryRenamed(editingCategory, newName);
      }
    } catch (error) {
      console.error('Failed to rename category:', error);
      alert(error instanceof Error ? error.message : 'Failed to rename category');
    }
  };

  const cancelEditing = () => {
    setEditingCategory(null);
    setEditingName('');
  };


  const getDotColor = (category: string) => {
    const color = CATEGORY_COLORS[category] || 'gray';
    
    const styles = {
      blue: 'bg-blue-500 dark:bg-blue-300',
      green: 'bg-green-500 dark:bg-green-300',
      red: 'bg-red-500 dark:bg-red-300',
      purple: 'bg-purple-500 dark:bg-purple-300',
      yellow: 'bg-yellow-500 dark:bg-yellow-300',
      gray: 'bg-gray-500 dark:bg-gray-300',
    };
    return styles[color as keyof typeof styles] || styles.gray;
  };

  const getIconColor = (category: string) => {
    const color = CATEGORY_COLORS[category] || 'gray';
    const iconColors = {
      blue: 'text-blue-700 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200',
      green: 'text-green-700 hover:text-green-800 dark:text-green-300 dark:hover:text-green-200',
      red: 'text-red-700 hover:text-red-800 dark:text-red-300 dark:hover:text-red-200',
      purple: 'text-purple-700 hover:text-purple-800 dark:text-purple-300 dark:hover:text-purple-200',
      yellow: 'text-yellow-700 hover:text-yellow-800 dark:text-yellow-300 dark:hover:text-yellow-200',
      gray: 'text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200',
    };
    return iconColors[color as keyof typeof iconColors] || iconColors.gray;
  };

  const getBorderStyle = (category: string) => {
    const isSelected = selectedCategory === category;
    
    // Use theme-based border colors instead of category colors
    return isSelected ? 'border-gray-900 dark:border-white border-2' : 'border-transparent border-2';
  };

  // Track previous open state to detect changes
  const [prevOpen, setPrevOpen] = useState(false);

  return (
    <Popover className="relative">
      {({ open, close }) => {
        // Notify parent when open state changes
        if (open !== prevOpen) {
          setPrevOpen(open);
          if (onOpenChange) {
            onOpenChange(open);
          }
        }

        return (
        <>
          <Popover.Button as={Fragment}>
            {React.cloneElement(trigger, { /* Pass props to trigger if needed */ })}
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-[1100] mt-2 w-64 transform origin-top sm:px-0 max-w-[calc(100vw-2rem)] sm:max-w-md right-2">
              {/* We manually trigger category loading on mount or potentially on open event if needed */}
              {/* Using useEffect([]) for now */}
              <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white dark:ring-opacity-10">
                <div className="relative grid gap-6 bg-white dark:bg-dark-card p-4">
                  {/* Header with Title and Close Button */}
                  <div className="flex items-center justify-between -mt-2 -mx-2 p-2 border-b border-gray-200 dark:border-dark-border">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-dark-secondary">
                      Filter by Category
                    </h3>
                    <button
                      onClick={close}
                      className="text-gray-400 hover:text-gray-500 dark:text-dark-secondary dark:hover:text-dark-primary focus:outline-none"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Remove the Clear Filter Option - replaced by toggle logic */}
                  {/* <div
                      className={`-m-3 flex items-center rounded-lg p-2 transition duration-150 ease-in-out cursor-pointer ${
                        !selectedCategory
                          ? 'bg-blue-50 dark:bg-blue-900/10'
                          : 'hover:bg-gray-100 dark:hover:bg-dark-hover'
                      }`}
                      onClick={() => handleCategoryClick(undefined, close)}
                  >
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-dark-primary">
                        Show All (No Filter)
                      </p>
                    </div>
                  </div> */}


                  {/* Category List */}
                  {categories.map((category) => {
                    const isEditing = editingCategory === category;

                    if (isEditing) {
                      return (
                        <form
                          key={category}
                          onSubmit={handleRenameCategory}
                          className={`-m-3 flex items-center justify-between rounded-lg p-2 transition duration-150 ease-in-out bg-gray-100 dark:bg-dark-hover ${getBorderStyle(category)}`}
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            {category === "Uncategorized" ? (
                              <span className="inline-block h-2 w-2 rounded-full bg-gray-500 dark:bg-gray-300 mr-2 flex-shrink-0" />
                            ) : (
                              <span
                                className={`inline-block h-2 w-2 rounded-full ${getDotColor(
                                  category
                                )} mr-2 flex-shrink-0`}
                              />
                            )}
                            <div className="ml-2 flex-1 min-w-0">
                              <input
                                ref={editInputRef}
                                type="text"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onBlur={cancelEditing}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') {
                                    cancelEditing();
                                  }
                                }}
                                className="w-full bg-transparent border-none outline-none text-sm font-medium text-gray-900 dark:text-dark-primary"
                                placeholder="Category name"
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-1 flex-shrink-0">
                            <button
                              type="submit"
                              onMouseDown={(e) => e.preventDefault()}
                              className="p-1 text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100 opacity-70 hover:opacity-100 transition-opacity duration-150"
                              aria-label="Save category name"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditing}
                              className="p-1 text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100 opacity-70 hover:opacity-100 transition-opacity duration-150"
                              aria-label="Cancel editing"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </form>
                      );
                    }

                    return (
                      <div
                        key={category}
                        onMouseEnter={() => setHoverCategory(category)}
                        onMouseLeave={() => setHoverCategory(null)}
                        className={`-m-3 flex items-center justify-between rounded-lg p-2 transition duration-150 ease-in-out cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-hover ${getBorderStyle(category)}`}
                        onClick={() => handleCategoryClick(category, close)}
                      >
                        <div className="flex items-center">
                          {category === "Uncategorized" ? (
                            <span className="inline-block h-2 w-2 rounded-full bg-gray-500 dark:bg-gray-300 mr-2 flex-shrink-0" />
                          ) : (
                            <span
                              className={`inline-block h-2 w-2 rounded-full ${getDotColor(
                                category
                              )} mr-2 flex-shrink-0`}
                            />
                          )}
                          <div className="ml-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-dark-primary">
                              {category}
                            </p>
                          </div>
                        </div>
                        
                        {/* Icons container with fixed space for consistent layout */}
                        {/* Fixed width. Apply flex-row-reverse ONLY when NOT selected */}
                        {/* to push the trash icon placeholder visually to the right. */}
                        <div className={`flex items-center space-x-1 w-[40px] justify-end ${
                          selectedCategory !== category ? 'flex-row-reverse' : ''
                        }`}>
                          {/* Conditional positioning based on selection state */}
                          {selectedCategory === category ? (
                            // When selected: show edit button first, then checkmark
                            <>
                              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                {hoverCategory === category && category !== "Uncategorized" && (
                                  <button
                                    onClick={(e) => handleEditCategory(e, category, close)}
                                    className={`p-1 ${getIconColor(category)} opacity-70 hover:opacity-100 transition-opacity duration-150 z-20`}
                                    aria-label={`Edit ${category} category`}
                                    onPointerDown={(e) => e.stopPropagation()}
                                  >
                                    <PencilIcon className="h-4 w-4" /> 
                                  </button>
                                )}
                              </div>
                              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                {HiOutlineCheck({ className: "w-6 h-6 text-gray-900 dark:text-white" })}
                              </div>
                            </>
                          ) : (
                            // When not selected: edit button takes checkmark position
                            <div className="w-10 h-5 flex items-center justify-end flex-shrink-0">
                              {hoverCategory === category && category !== "Uncategorized" && (
                                <button
                                  onClick={(e) => handleEditCategory(e, category, close)}
                                  className={`p-1 ${getIconColor(category)} opacity-70 hover:opacity-100 transition-opacity duration-150 z-20`}
                                  aria-label={`Edit ${category} category`}
                                  onPointerDown={(e) => e.stopPropagation()}
                                >
                                  <PencilIcon className="h-4 w-4" /> 
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Optional: Add "Add new category" button here if needed in filter context */}
              </div>
            </Popover.Panel>
          </Transition>
        </>
        );
      }}
    </Popover>
  );
};

export default CategoryFilterPopup; 